"use client";

import { signIn } from "@/lib/auth/client";
import { useSearchParams } from "next/navigation";
import { useState } from "react";

/**
 * LoginForm — Client Component.
 *
 * Isolated here so the parent page.tsx can wrap this component in <Suspense>,
 * satisfying Next.js 15+ static prerendering requirements for useSearchParams().
 */
export function LoginForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const searchParams = useSearchParams();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage(null);

    const callbackUrl = searchParams.get("callbackUrl") ?? "/dashboard/cost";
    const result = await signIn.email({ email, password });

    if ((result as { error?: unknown } | undefined)?.error) {
      setErrorMessage("Invalid email or password. Please try again.");
      setIsSubmitting(false);
      return;
    }

    window.location.href = callbackUrl;
  };

  const handleGoogleSignIn = async () => {
    await signIn.social({ provider: "google" });
  };

  return (
    <div className="mx-auto w-full max-w-md px-4 py-10">
      <h1 className="mb-6 text-2xl font-bold">Sign In</h1>

      <button
        onClick={handleGoogleSignIn}
        className="mb-4 flex w-full items-center justify-center gap-2 rounded-lg border p-3 hover:bg-gray-50"
      >
        Sign in with Google
      </button>

      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="bg-white px-2 text-gray-500">
            Or continue with email
          </span>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="email" className="mb-1 block text-sm font-medium">
            Email
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-lg border p-3"
            required
          />
        </div>
        <div>
          <label htmlFor="password" className="mb-1 block text-sm font-medium">
            Password
          </label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-lg border p-3"
            required
          />
        </div>
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full rounded-lg bg-blue-600 p-3 text-white hover:bg-blue-700"
        >
          {isSubmitting ? "Signing in..." : "Sign In"}
        </button>
        {errorMessage ? (
          <p className="text-sm text-red-600">{errorMessage}</p>
        ) : null}
      </form>
    </div>
  );
}
